import { Editor } from "@tiptap/react";
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  MutableRefObject,
} from "react";
import { createPortal } from "react-dom";
import UndoIcon from "../../../assets/svg/v1/undo-icon";
import CheckIcon from "../../../assets/svg/v1/check-icon";
import FontSizeIcon from "../../../assets/svg/v1/font-size-icon";
import ItalicIcon from "../../../assets/svg/v1/italic-icon";
import UnderlineIcon from "../../../assets/svg/underline-icon";
import { TextSelection } from "prosemirror-state";
import {
  searchEmojiItems,
  getPopularEmojiItems,
  type EmojiItem,
} from "../../../utils/functions/emoji-extractor";
import { emojiList } from "../../../utils/constants/emojiList";
import { v4 as uuidv4 } from "uuid";
import AttachFilesIcon from "../../../assets/svg/v1/attach-files-icon";
import { customToast } from "../../../utils/custom-toast/custom-toast";
import ChevronDownIcon from "../../../assets/svg/chevron-down-icon";
import AttachmentIcon from "../../../assets/svg/v1/attachment-icon";
import { Tooltip } from "../../../components/Tooltip/Tooltip";
import BoldIcon from "../../../assets/svg/bold-icon";
import RedoIcon from "../../../assets/svg/v1/redo-icon";
import BulletedListIcon from "../../../assets/svg/v1/bulleted-list-icon";
import NumberListIcon from "../../../assets/svg/v1/number-list-icon";
import AlignLeftIcon from "../../../assets/svg/v1/align-left-icon";
import AlignCenterIcon from "../../../assets/svg/v1/align-center-icon";
import AlignRightIcon from "../../../assets/svg/v1/align-right-icon";
import RemoveStyleIcon from "../../../assets/svg/v1/remove-style-icon";
import StrikeThroughIcon from "../../../assets/svg/strikethrough-icon";
import QuoteIcon from "../../../assets/svg/v1/quote-icon";
import TextColorPickerIcon from "../../../assets/svg/v1/text-color-picker-icon";
import IndentRightIcon from "../../../assets/svg/v1/indent-right-icon";
import IndentLeftIcon from "../../../assets/svg/v1/indent-left-icon";
import EmojiIcon from "../../../assets/svg/emoji-icon";
import ImageIcon from "../../../assets/svg/v1/image-icon";

const isColorLight = (hexColor: string): boolean => {
  const hex = hexColor.replace("#", "");
  if (hex.length !== 6) {
    return false;
  }
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5;
};

const ColorPicker = ({
  editor,
  onSetColor,
  onUnsetColor,
}: {
  editor: Editor;
  onSetColor: (color: string) => void;
  onSetHighlight: (color: string) => void;
  onUnsetColor: () => void;
  onUnsetHighlight: () => void;
}) => {
  const grayscale = [
    "#000000",
    "#434343",
    "#666666",
    "#999999",
    "#b7b7b7",
    "#cccccc",
    "#efefef",
  ];
  const palette = [
    "#ff0000",
    "#ff9900",
    "#ffff00",
    "#00ff00",
    "#00ffff",
    "#0000ff",
    "#9900ff",
    "#ff00ff",
  ];
  const tones = [
    [
      "#FADBD8",
      "#FAE5D3",
      "#FCF3CF",
      "#D4EFDF",
      "#D6EAF8",
      "#EBDEF0",
      "#EAECEE",
      "#D6DBDF",
    ],
    [
      "#F5B7B1",
      "#F5CBA7",
      "#F9E79F",
      "#A9DFBF",
      "#AED6F1",
      "#D2B4DE",
      "#D7DBDD",
      "#BFC9CA",
    ],
    [
      "#F1948A",
      "#F0B27A",
      "#F7DC6F",
      "#7DCEA0",
      "#85C1E9",
      "#BB8FCE",
      "#B2BABB",
      "#AAB7B8",
    ],
    [
      "#EC7063",
      "#EB984E",
      "#F4D03F",
      "#52BE80",
      "#5DADE2",
      "#A569BD",
      "#808B96",
      "#7F8C8D",
    ],
    [
      "#E74C3C",
      "#E67E22",
      "#F1C40F",
      "#27AE60",
      "#3498DB",
      "#8E44AD",
      "#566573",
      "#515A5A",
    ],
    [
      "#b03a2e",
      "#af601a",
      "#b7950b",
      "#1e8449",
      "#2874a6",
      "#6c3483",
      "#2e4053",
      "#212f3d",
    ],
  ];

  // Helper function to get current color from editor
  const getCurrentColor = (): string | null => {
    try {
      const colorAttr = editor.getAttributes("color");
      const textStyleAttr = editor.getAttributes("textStyle");
      return colorAttr?.color || textStyleAttr?.color || null;
    } catch {
      return null;
    }
  };

  const normalizeColor = (color: string | null): string | null => {
    if (!color) return null;
    return color.toLowerCase();
  };

  const colorsMatch = (
    color1: string | null,
    color2: string | null
  ): boolean => {
    return normalizeColor(color1) === normalizeColor(color2);
  };

  const ColorGrid = ({
    colors,
    onSelect,
    isActive,
    onClear,
    hasClear,
  }: {
    colors: string[];
    onSelect: (color: string) => void;
    isActive: (color: string | null) => boolean;
    onClear?: () => void;
    hasClear?: boolean;
  }) => {
    return (
      <div className="grid grid-cols-8 gap-1 w-[200px]">
        {hasClear && (
          <button
            onClick={onClear}
            className="w-4 h-4 rounded-sm bg-pure-white dark:bg-verba-indigo border border-border-gray-fixed dark:border-border-gray-fixed-dk flex items-center justify-center"
          >
            {isActive(null) && <CheckIcon className="w-4 h-4 text-black" />}
          </button>
        )}
        {colors.map((color) => (
          <button
            key={color}
            onClick={() => onSelect(color)}
            className="w-4 h-4 rounded-sm bg-pure-white dark:bg-verba-indigo border border-border-gray-fixed dark:border-border-gray-fixed-dk hover:border-border-gray-fixed-dk dark:hover:border-border-gray-fixed transition-colors"
            style={{ backgroundColor: color }}
          >
            {isActive(color) && (
              <CheckIcon
                className={`w-4 h-4 ${
                  isColorLight(color) ? "text-black" : "text-white"
                }`}
              />
            )}
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="p-2">
      <div>
        <p className="text-sm font-medium mb-2">Text colour</p>
        <div className="space-y-1">
          <ColorGrid
            colors={grayscale}
            onSelect={onSetColor}
            isActive={(color: string | null) =>
              colorsMatch(getCurrentColor(), color)
            }
            onClear={onUnsetColor}
            hasClear
          />
          <ColorGrid
            colors={palette}
            onSelect={onSetColor}
            isActive={(color: string | null) =>
              colorsMatch(getCurrentColor(), color)
            }
          />
          {tones.map((row, i) => (
            <ColorGrid
              key={i}
              colors={row}
              onSelect={onSetColor}
              isActive={(color: string | null) =>
                colorsMatch(getCurrentColor(), color)
              }
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const getInitialEmojis = () =>
  getPopularEmojiItems(emojiList, 100).slice(0, 100);

export const EmojiPicker = ({
  editor,
  onClose,
}: {
  editor: Editor;
  onClose: () => void;
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [filteredEmojis, setFilteredEmojis] =
    useState<EmojiItem[]>(getInitialEmojis);

  useEffect(() => {
    if (searchTerm.trim()) {
      setFilteredEmojis(searchEmojiItems(emojiList, searchTerm).slice(0, 100));
    } else if (selectedCategory) {
      setFilteredEmojis(
        emojiList
          .filter((emoji) => emoji.category === selectedCategory)
          .slice(0, 100)
      );
    } else {
      setFilteredEmojis(getInitialEmojis());
    }
  }, [selectedCategory, searchTerm]);

  const handleEmojiClick = (emoji: EmojiItem) => {
    editor.chain().focus().insertContent(emoji.emoji).run();
    onClose();
  };

  const getCategoryTabs = () => {
    const categories = [...new Set(emojiList.map((emoji) => emoji.category))];
    const filteredCategories = categories.filter(
      (category) => category !== "Component"
    );
    return filteredCategories.map((category) => ({
      name: category,
      label: category,
    }));
  };

  return (
    <div
      className="p-2 w-[400px] bg-fixed-white-light-and-dark-mode dark:bg-fixed-white-light-and-dark-mode-dk rounded-[4px] shadow-popper-blur-effect"
      data-emoji-popup-container
    >
      {/* Search Bar */}
      <div className="mb-3">
        <input
          type="text"
          placeholder="Search emojis"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-3 py-2 border border-border-gray-fixed dark:border-border-gray-fixed-dk rounded-[6px] text-xs text-verba-indigo dark:text-verba-indigo bg-fixed-white dark:bg-fixed-white-dk focus:outline-none focus:border-verba-blue dark:focus:border-verba-blue-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk transition-colors"
        />
      </div>

      {/* Category Tabs */}
      {!searchTerm && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          <button
            onClick={() => setSelectedCategory("")}
            className={`px-2.5 py-1 text-xs rounded-[6px] font-medium transition-colors ${
              selectedCategory === ""
                ? "bg-verba-blue dark:bg-verba-blue-dk text-fixed-white dark:text-fixed-white-dk"
                : "text-verba-indigo dark:text-verba-indigo hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
            }`}
          >
            Popular
          </button>
          {getCategoryTabs().map((tab) => (
            <button
              key={tab.name}
              onClick={() => setSelectedCategory(tab.name)}
              className={`px-2.5 py-1 text-xs rounded-[6px] font-medium transition-colors ${
                selectedCategory === tab.name
                  ? "bg-verba-blue dark:bg-verba-blue-dk text-fixed-white dark:text-fixed-white-dk"
                  : "text-verba-indigo dark:text-verba-indigo hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-9 gap-x-1 gap-y-1 max-h-64 overflow-y-auto no-scrollbar">
        {filteredEmojis.map((emoji, index) => (
          <button
            key={`${emoji.code.join("-")}-${index}`}
            onClick={() => handleEmojiClick(emoji)}
            className="w-[32px] h-[32px] flex items-center justify-center text-[22px] hover:bg-hover-effect dark:hover:bg-hover-effect-dk rounded-[6px] cursor-pointer transition-colors"
            title={emoji.name}
          >
            {emoji.emoji}
          </button>
        ))}
      </div>

      {filteredEmojis.length === 0 && (
        <div className="text-center text-gray-placeholder dark:text-gray-placeholder-dk text-xs py-4">
          No emojis found
        </div>
      )}
    </div>
  );
};

interface Attachment {
  id: string;
  name: string;
  size?: number;
  type?: string;
  data?: Blob;
}
export interface ToolbarSchema {
  // History controls
  history?: boolean;

  // Typography controls
  typography?: {
    fontFamily?: boolean;
    fontSize?: boolean;
  };

  // Text formatting
  textFormatting?: {
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    strikethrough?: boolean;
  };

  // Text styling
  textStyling?: {
    textColor?: boolean;
  };

  // Content structure
  contentStructure?: {
    lists?: boolean;
    alignment?: boolean;
    indentation?: boolean;
    blockquotes?: boolean;
  };

  // Media and attachments
  media?: {
    links?: boolean;
    images?: boolean;
    attachments?: boolean;
  };

  // Interactive elements
  interactive?: {
    emojis?: boolean;
  };

  // Utility tools
  utilities?: {
    clearFormatting?: boolean;
  };
}

const defaultSchema: ToolbarSchema = {
  history: true,
  typography: {
    fontFamily: true,
    fontSize: true,
  },
  textFormatting: {
    bold: true,
    italic: true,
    underline: true,
    strikethrough: true,
  },
  textStyling: {
    textColor: true,
  },
  contentStructure: {
    lists: true,
    alignment: true,
    indentation: true,
    blockquotes: true,
  },
  media: {
    links: true,
    images: true,
    attachments: true,
  },
  interactive: {
    emojis: true,
  },
  utilities: {
    clearFormatting: true,
  },
};

const useToolbarPortal = (
  isOpen: boolean,
  triggerRef: React.RefObject<HTMLElement>,
  onClose: () => void,
  dropdownWidth: number = 200,
  dropdownHeightHint: number = 300
) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [placement, setPlacement] = useState<"top" | "bottom">("bottom");
  const [positioned, setPositioned] = useState(false);
  const portalRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const buffer = 8;

    const actualHeight = portalRef.current?.offsetHeight || dropdownHeightHint;

    const spaceRight = viewportWidth - rect.left - buffer;
    const spaceLeft = rect.left - buffer;

    const shouldPlaceLeft =
      spaceRight < dropdownWidth && spaceLeft > dropdownWidth;

    setPlacement("top");

    let top: number;
    let left: number;

    top = rect.top - actualHeight - buffer;

    if (shouldPlaceLeft) {
      left = rect.right - dropdownWidth;
    } else {
      left = rect.left;
    }

    left = Math.max(
      buffer,
      Math.min(left, viewportWidth - dropdownWidth - buffer)
    );
    top = Math.max(buffer, Math.min(top, viewportHeight - actualHeight - buffer));

    setPosition({ top, left });
    setPositioned(true);
  }, [triggerRef, dropdownWidth, dropdownHeightHint]);

  useEffect(() => {
    if (!isOpen) {
      setPositioned(false);
      return;
    }
    if (!triggerRef.current) return;

    updatePosition();

    const handleUpdate = () => updatePosition();
    window.addEventListener("resize", handleUpdate);

    const blockScroll = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
    };
    window.addEventListener("scroll", blockScroll, {
      passive: false,
      capture: true,
    });
    window.addEventListener("wheel", blockScroll, {
      passive: false,
      capture: true,
    });
    window.addEventListener("touchmove", blockScroll, {
      passive: false,
      capture: true,
    });

    return () => {
      window.removeEventListener("resize", handleUpdate);
      window.removeEventListener("scroll", blockScroll, true);
      window.removeEventListener("wheel", blockScroll, true);
      window.removeEventListener("touchmove", blockScroll, true);
    };
  }, [isOpen, triggerRef, updatePosition]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (triggerRef.current && !triggerRef.current.contains(target)) {
        const portalElements = document.querySelectorAll(
          "[data-toolbar-portal]"
        );
        let isInsidePortal = false;

        for (const element of portalElements) {
          if (element.contains(target)) {
            isInsidePortal = true;
            break;
          }
        }

        if (!isInsidePortal) {
          onClose();
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose, triggerRef]);

  useEffect(() => {
    if (!isOpen) return;

    const isInsideToolbarPortal = (node: EventTarget | null) =>
      node instanceof Element && !!node.closest("[data-toolbar-portal]");

    const guardFocusEvent = (event: FocusEvent) => {
      if (
        isInsideToolbarPortal(event.target) ||
        isInsideToolbarPortal(event.relatedTarget)
      ) {
        event.stopImmediatePropagation();
      }
    };

    document.addEventListener("focusin", guardFocusEvent, true);
    document.addEventListener("focusout", guardFocusEvent, true);
    return () => {
      document.removeEventListener("focusin", guardFocusEvent, true);
      document.removeEventListener("focusout", guardFocusEvent, true);
    };
  }, [isOpen]);

  const portalRefCallback = useCallback(
    (node: HTMLDivElement | null) => {
      (portalRef as MutableRefObject<HTMLDivElement | null>).current = node;
      if (node) requestAnimationFrame(updatePosition);
    },
    [updatePosition]
  );

  const renderPortal = (children: React.ReactNode) => {
    if (!isOpen) return null;

    return createPortal(
      <div
        ref={portalRefCallback}
        data-toolbar-portal
        style={{
          position: "fixed",
          top: position.top,
          left: position.left,
          zIndex: 9999999,
          pointerEvents: "auto",
          visibility: positioned ? "visible" : "hidden",
        }}
      >
        {children}
      </div>,
      document.body
    );
  };

  return { renderPortal, placement };
};

const Toolbar = ({
  editor,
  onAttachmentsChange,
  schema = defaultSchema,
  toolbarExtraClass,
}: {
  editor: Editor;
  onAttachmentsChange?: (attachments: Attachment[]) => void;
  schema?: ToolbarSchema;
  toolbarExtraClass?: string;
}) => {
  const [isFontOpen, setIsFontOpen] = useState(false);
  const [isSizeOpen, setIsSizeOpen] = useState(false);
  const [isColorOpen, setIsColorOpen] = useState(false);
  const [isListOpen, setIsListOpen] = useState(false);
  const [isAlignOpen, setIsAlignOpen] = useState(false);
  const [isLinkPopoverOpen, setIsLinkPopoverOpen] = useState(false);
  const [isEmojiOpen, setIsEmojiOpen] = useState(false);
  const [isProcessingAttachments, setIsProcessingAttachments] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");
  const [selectedFont, setSelectedFont] = useState("sans-serif");
  const [selectedSize, setSelectedSize] = useState("normal");
  const [selectedList, setSelectedList] = useState("bullet");
  const [selectedAlign, setSelectedAlign] = useState("left");
  const fontDropdownRef = useRef<HTMLDivElement>(null);
  const sizeDropdownRef = useRef<HTMLDivElement>(null);
  const colorDropdownRef = useRef<HTMLDivElement>(null);
  const listDropdownRef = useRef<HTMLDivElement>(null);
  const alignDropdownRef = useRef<HTMLDivElement>(null);
  const linkPopoverRef = useRef<HTMLDivElement>(null);
  const emojiDropdownRef = useRef<HTMLDivElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const [linkUrlError, setLinkUrlError] = useState<string>("");

  // Add state to track active formatting for future text
  const [activeFormatting, setActiveFormatting] = useState<{
    bold: boolean;
    italic: boolean;
    underline: boolean;
    strike: boolean;
    color: string | null;
    fontFamily: string | null;
    fontSize: string | null;
  }>({
    bold: false,
    italic: false,
    underline: false,
    strike: false,
    color: null,
    fontFamily: null,
    fontSize: null,
  });

  const MAX_IMAGE_SIZE_BYTES = 2 * 1024 * 1024;
  const MAX_IMAGE_SIZE_MB = 2;

  const calculateTotalImageSize = useCallback(() => {
    if (!editor) return 0;

    let totalSize = 0;
    const { doc } = editor.state;

    doc.descendants((node) => {
      if (node.type.name === "image" && node.attrs.src) {
        if (node.attrs.src.startsWith("data:")) {
          const base64Data = node.attrs.src.split(",")[1];
          if (base64Data) {
            totalSize += Math.ceil((base64Data.length * 3) / 4);
          }
        }
      }
      return true;
    });

    return totalSize;
  }, [editor]);

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const mergedSchema = { ...defaultSchema, ...schema };
  const typographySchema = {
    ...defaultSchema.typography,
    ...schema.typography,
  };
  const textFormattingSchema = {
    ...defaultSchema.textFormatting,
    ...schema.textFormatting,
  };
  const textStylingSchema = {
    ...defaultSchema.textStyling,
    ...schema.textStyling,
  };
  const contentStructureSchema = {
    ...defaultSchema.contentStructure,
    ...schema.contentStructure,
  };
  const mediaSchema = { ...defaultSchema.media, ...schema.media };
  const interactiveSchema = {
    ...defaultSchema.interactive,
    ...schema.interactive,
  };
  const utilitiesSchema = { ...defaultSchema.utilities, ...schema.utilities };

  const fontPortal = useToolbarPortal(isFontOpen, fontDropdownRef, () =>
    setIsFontOpen(false)
  );
  const sizePortal = useToolbarPortal(isSizeOpen, sizeDropdownRef, () =>
    setIsSizeOpen(false)
  );
  const colorPortal = useToolbarPortal(
    isColorOpen,
    colorDropdownRef,
    () => setIsColorOpen(false),
    250,
    320
  );
  const listPortal = useToolbarPortal(
    isListOpen,
    listDropdownRef,
    () => setIsListOpen(false),
    60,
    60
  );
  const alignPortal = useToolbarPortal(
    isAlignOpen,
    alignDropdownRef,
    () => setIsAlignOpen(false),
    60,
    90
  );
  const linkPortal = useToolbarPortal(
    isLinkPopoverOpen,
    linkPopoverRef,
    () => setIsLinkPopoverOpen(false),
    320,
    180
  );
  const emojiPortal = useToolbarPortal(
    isEmojiOpen,
    emojiDropdownRef,
    () => setIsEmojiOpen(false),
    300,
    400
  );

  const fontOptions = [
    { value: "sans-serif", label: "Sans Serif" },
    { value: "serif", label: "Serif" },
    { value: "monospace", label: "Fixed Width" },
    { value: "arial black", label: "Wide" },
    { value: "arial narrow", label: "Narrow" },
    { value: "comic sans ms", label: "Comic Sans MS" },
    { value: "garamond", label: "Garamond" },
    { value: "georgia", label: "Georgia" },
    { value: "tahoma", label: "Tahoma" },
    { value: "trebuchet ms", label: "Trebuchet MS" },
    { value: "verdana", label: "Verdana" },
  ];

  const sizeOptions = [
    { value: "small", label: "Small", fontSize: "12px" },
    { value: "normal", label: "Normal", fontSize: "14px" },
    { value: "large", label: "Large", fontSize: "18px" },
    { value: "huge", label: "Huge", fontSize: "24px" },
  ];

  const listOptions = [
    { value: "bullet", icon: BulletedListIcon },
    { value: "number", icon: NumberListIcon },
  ];

  const alignOptions = [
    { value: "left", icon: AlignLeftIcon },
    { value: "center", icon: AlignCenterIcon },
    { value: "right", icon: AlignRightIcon },
  ];

  const formattingOptions = [
    {
      value: "remove",
      icon: RemoveStyleIcon,
      action: () => editor.chain().focus().clearNodes().unsetAllMarks().run(),
      iconClassName:
        "w-5 h-5 text-dark-gray-fixed dark:text-dark-gray-fixed-dk",
    },
    {
      value: "strike",
      icon: StrikeThroughIcon,
      action: () => editor.chain().focus().toggleStrike().run(),
      iconClassName:
        "w-5 h-5 text-dark-gray-fixed dark:text-dark-gray-fixed-dk",
    },
    {
      value: "quote",
      icon: QuoteIcon,
      action: () => editor.chain().focus().toggleBlockquote().run(),
      iconClassName:
        "w-5 h-5 text-dark-gray-fixed dark:text-dark-gray-fixed-dk",
    },
  ];

  useEffect(() => {
    if (editor) {
      if (editor.isActive("bulletList")) {
        setSelectedList("bullet");
      } else if (editor.isActive("orderedList")) {
        setSelectedList("number");
      }
    }
  }, [editor, editor?.state.selection]);

  // Update active formatting state when editor selection changes
  useEffect(() => {
    if (editor) {
      const currentFontSize =
        editor.getAttributes("textStyle")?.fontSize || null;

      if (currentFontSize) {
        const matchingSizeOption = sizeOptions.find(
          (option) => option.fontSize === currentFontSize
        );
        if (matchingSizeOption) {
          setSelectedSize(matchingSizeOption.value);
        }
      } else {
        setSelectedSize("normal");
      }

      setActiveFormatting({
        bold: editor.isActive("bold"),
        italic: editor.isActive("italic"),
        underline: editor.isActive("underline"),
        strike: editor.isActive("strike"),
        color: editor.getAttributes("color")?.color || null,
        fontFamily: editor.getAttributes("textStyle")?.fontFamily || null,
        fontSize: currentFontSize,
      });
    }
  }, [editor, editor?.state.selection]);

  const handleFontChange = (fontValue: string) => {
    setSelectedFont(fontValue);
    setActiveFormatting((prev) => ({ ...prev, fontFamily: fontValue }));
    editor.chain().focus().setFontFamily(fontValue).run();
    setIsFontOpen(false);
  };

  const handleSizeChange = (sizeValue: string) => {
    setSelectedSize(sizeValue);
    const sizeOption = sizeOptions.find((option) => option.value === sizeValue);
    if (sizeOption) {
      setActiveFormatting((prev) => ({
        ...prev,
        fontSize: sizeOption.fontSize,
      }));
      editor.chain().focus().setFontSize(sizeOption.fontSize).run();
    }
    setIsSizeOpen(false);
  };

  const handleListChange = (listValue: string) => {
    setSelectedList(listValue);

    if (listValue === "bullet") {
      if (editor.isActive("bulletList")) {
        editor.chain().focus().toggleBulletList().run();
      } else if (editor.isActive("orderedList")) {
        editor.chain().focus().toggleOrderedList().toggleBulletList().run();
      } else {
        editor.chain().focus().toggleBulletList().run();
      }
    } else if (listValue === "number") {
      if (editor.isActive("orderedList")) {
        editor.chain().focus().toggleOrderedList().run();
      } else if (editor.isActive("bulletList")) {
        editor.chain().focus().toggleBulletList().toggleOrderedList().run();
      } else {
        editor.chain().focus().toggleOrderedList().run();
      }
    }
    setIsListOpen(false);
  };

  const handleAlignChange = (alignValue: string) => {
    setSelectedAlign(alignValue);

    const { from, to } = editor.state.selection;
    const hasSelection = from !== to;

    if (hasSelection) {
      editor
        .chain()
        .focus()
        .setTextAlign(alignValue as "left" | "center" | "right")
        .run();
    } else {
      const { doc } = editor.state;

      const fullRange = { from: 0, to: doc.content.size };

      editor
        .chain()
        .focus()
        .setTextSelection(fullRange)
        .setTextAlign(alignValue as "left" | "center" | "right")
        .run();

      const tr = editor.state.tr;
      let modified = false;

      doc.descendants((node, pos) => {
        if (node.type.name === "image") {
          tr.setNodeMarkup(
            pos,
            undefined,
            {
              ...node.attrs,
              textAlign: alignValue,
            },
            undefined
          );
          modified = true;
        }
        return true;
      });

      if (modified) {
        editor.view.dispatch(tr);
      }

      editor.chain().focus().setTextSelection(0).run();
    }

    setIsAlignOpen(false);
  };

  const handleSetColor = (color: string) => {
    const { from, to } = editor.state.selection;
    setActiveFormatting((prev) => ({ ...prev, color }));
    editor
      .chain()
      .focus()
      .setTextSelection({ from, to })
      .setColor(color)
      .run();
    setIsColorOpen(false);
  };

  const handleUnsetColor = () => {
    const { from, to } = editor.state.selection;
    setActiveFormatting((prev) => ({ ...prev, color: null }));
    editor
      .chain()
      .focus()
      .setTextSelection({ from, to })
      .unsetColor()
      .run();
    setIsColorOpen(false);
  };

  const handleSetHighlight = (color: string) => {
    editor.chain().toggleHighlight({ color }).run();
    setIsColorOpen(false);
  };

  const handleUnsetHighlight = () => {
    editor.chain().unsetHighlight().run();
    setIsColorOpen(false);
  };

  const handleColorButtonClick = () => {
    setIsColorOpen(!isColorOpen);
  };

  const validateUrl = useCallback(
    (url: string): { isValid: boolean; error?: string } => {
      const trimmedUrl = url.trim();

      if (!trimmedUrl) {
        return { isValid: false, error: "URL is required" };
      }

      try {
        const urlObj = new URL(trimmedUrl);
        const validProtocols = ["http:", "https:", "mailto:", "tel:", "ftp:"];
        if (!validProtocols.includes(urlObj.protocol)) {
          return {
            isValid: false,
            error:
              "Invalid protocol. Use http://, https://, mailto:, tel:, or ftp://",
          };
        }

        if (
          (urlObj.protocol === "http:" || urlObj.protocol === "https:") &&
          !urlObj.hostname
        ) {
          return { isValid: false, error: "Invalid URL format" };
        }

        return { isValid: true };
      } catch {
        let fixedUrl = trimmedUrl;

        if (!/^[a-zA-Z]+:\/\//.test(fixedUrl)) {
          fixedUrl = "https://" + fixedUrl;
        }

        try {
          new URL(fixedUrl);
          return { isValid: true };
        } catch {
          return { isValid: false, error: "Please enter a valid URL" };
        }
      }
    },
    []
  );

  const handleLinkButtonClick = useCallback(() => {
    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to, " ");
    setLinkText(selectedText);
    setLinkUrl("");
    setLinkUrlError("");

    setIsLinkPopoverOpen((prev) => !prev);
  }, [editor]);

  const handleApplyLink = useCallback(() => {
    const url = linkUrl.trim();
    const text = linkText.trim();
    const { from, to } = editor.state.selection;
    const range = { from, to };

    if (!url || !text) {
      if (!url) {
        setLinkUrlError("URL is required");
      }
      if (!text) {
        return;
      }
      return;
    }

    const validation = validateUrl(url);
    if (!validation.isValid) {
      setLinkUrlError(validation.error || "Invalid URL");
      return;
    }

    setLinkUrlError("");

    let normalizedUrl = url;
    if (!/^[a-zA-Z]+:\/\//.test(normalizedUrl)) {
      normalizedUrl = "https://" + normalizedUrl;
    }

    editor
      .chain()
      .focus()
      .insertContentAt(range, {
        type: "text",
        text: text,
        marks: [
          {
            type: "link",
            attrs: { href: normalizedUrl },
          },
        ],
      })
      .setTextSelection(range.from + text.length)
      .unsetMark("link")
      .run();

    setIsLinkPopoverOpen(false);
  }, [editor, linkUrl, linkText, validateUrl]);

  const handleLinkUrlChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newUrl = e.target.value;
      setLinkUrl(newUrl);

      if (linkUrlError) {
        setLinkUrlError("");
      }
    },
    [linkUrlError]
  );

  const handleImageUpload = useCallback(() => {
    if (imageInputRef.current) {
      imageInputRef.current.click();
    }
  }, []);

  const handleImageFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file || !editor) {
        if (imageInputRef.current) {
          imageInputRef.current.value = "";
        }
        return;
      }

      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        customToast(
          `Image size (${formatBytes(
            file.size
          )}) exceeds the maximum allowed size of ${MAX_IMAGE_SIZE_MB}MB`,
          "error"
        );
        if (imageInputRef.current) {
          imageInputRef.current.value = "";
        }
        return;
      }

      const currentTotalSize = calculateTotalImageSize();
      const newTotalSize = currentTotalSize + file.size;

      if (newTotalSize > MAX_IMAGE_SIZE_BYTES) {
        const remainingSize = MAX_IMAGE_SIZE_BYTES - currentTotalSize;
        customToast(
          `Adding this image would exceed the ${MAX_IMAGE_SIZE_MB}MB limit. ` +
            `Current usage: ${formatBytes(currentTotalSize)}. ` +
            `Remaining: ${formatBytes(remainingSize)}`,
          "error"
        );
        if (imageInputRef.current) {
          imageInputRef.current.value = "";
        }
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        if (!e.target?.result) {
          return;
        }
        const dataUrl = e.target.result as string;
        const img = new window.Image();
        img.src = dataUrl;
        img.onload = () => {
          const editorWidth = editor.view.dom.clientWidth;
          const { naturalWidth } = img;

          const width =
            naturalWidth > editorWidth ? "100%" : `${naturalWidth}px`;

          editor
            .chain()
            .focus()
            .insertContent({
              type: "image",
              attrs: {
                src: dataUrl,
                width,
                originalSize: file.size,
              },
            })
            .command(({ tr }) => {
              const pos = tr.selection.to;
              tr.setSelection(TextSelection.create(tr.doc, pos));
              return true;
            })
            .focus()
            .run();
        };
      };
      reader.readAsDataURL(file);

      if (imageInputRef.current) {
        imageInputRef.current.value = "";
      }
    },
    [editor, calculateTotalImageSize]
  );

  const handleAttachmentFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files || []);

      if (files.length === 0) return;

      setIsProcessingAttachments(true);
      const processFiles = async () => {
        try {
          const uploaded: Attachment[] = [];

          for (const file of files) {
            uploaded.push({
              id: uuidv4(),
              name: file.name,
              size: file.size,
              type: file.type,
              data: file,
            });
          }
          onAttachmentsChange?.(uploaded);
        } catch (error) {
          console.error("Error processing attachments:", error);
        } finally {
          setIsProcessingAttachments(false);
        }
      };

      processFiles();

      if (attachmentInputRef.current) {
        attachmentInputRef.current.value = "";
      }
    },
    [onAttachmentsChange]
  );

  const handleAttachmentClick = useCallback(() => {
    if (attachmentInputRef.current) {
      attachmentInputRef.current.click();
    }
  }, []);

  const handleIndent = () => {
    const { from, to } = editor.state.selection;
    const hasSelection = from !== to;

    if (hasSelection) {
      editor.chain().focus().indent().run();
    } else {
      const { doc } = editor.state;

      const tr = editor.state.tr;
      let modified = false;

      doc.descendants((node, pos) => {
        if (node.isBlock || node.type.name === "image") {
          const currentLevel = node.attrs.indentLevel || 0;
          if (currentLevel < 100) {
            tr.setNodeMarkup(
              pos,
              undefined,
              {
                ...node.attrs,
                indentLevel: currentLevel + 1,
              },
              undefined
            );
            modified = true;
          }
        }
        return true;
      });

      if (modified) {
        const currentPos = editor.state.selection.to;

        tr.setSelection(TextSelection.create(tr.doc, currentPos));

        editor.view.dispatch(tr);
      } else {
        return;
      }
    }
  };

  const handleOutdent = () => {
    const { from, to } = editor.state.selection;
    const hasSelection = from !== to;

    if (hasSelection) {
      editor.chain().focus().outdent().run();
    } else {
      const { doc } = editor.state;

      const tr = editor.state.tr;
      let modified = false;

      doc.descendants((node, pos) => {
        if (node.isBlock || node.type.name === "image") {
          const currentLevel = node.attrs.indentLevel || 0;
          if (currentLevel > 0) {
            tr.setNodeMarkup(
              pos,
              undefined,
              {
                ...node.attrs,
                indentLevel: currentLevel - 1,
              },
              undefined
            );
            modified = true;
          }
        }
        return true;
      });

      if (modified) {
        const currentPos = editor.state.selection.to;

        tr.setSelection(TextSelection.create(tr.doc, currentPos));

        editor.view.dispatch(tr);
      } else {
        return;
      }
    }
  };

  const handleEmojiToggle = () => {
    setIsEmojiOpen(!isEmojiOpen);
  };

  // Update formatting handlers to toggle state for future text
  const handleBoldToggle = () => {
    const newBoldState = !activeFormatting.bold;
    setActiveFormatting((prev) => ({ ...prev, bold: newBoldState }));

    if (newBoldState) {
      editor.chain().focus().toggleBold().run();
    } else {
      editor.chain().focus().unsetBold().run();
    }
  };

  const handleItalicToggle = () => {
    const newItalicState = !activeFormatting.italic;
    setActiveFormatting((prev) => ({ ...prev, italic: newItalicState }));

    if (newItalicState) {
      editor.chain().focus().toggleItalic().run();
    } else {
      editor.chain().focus().unsetItalic().run();
    }
  };

  const handleUnderlineToggle = () => {
    const newUnderlineState = !activeFormatting.underline;
    setActiveFormatting((prev) => ({ ...prev, underline: newUnderlineState }));

    if (newUnderlineState) {
      editor.chain().focus().toggleUnderline().run();
    } else {
      editor.chain().focus().unsetUnderline().run();
    }
  };

  const handleStrikeToggle = () => {
    const newStrikeState = !activeFormatting.strike;
    setActiveFormatting((prev) => ({ ...prev, strike: newStrikeState }));

    if (newStrikeState) {
      editor.chain().focus().toggleStrike().run();
    } else {
      editor.chain().focus().unsetStrike().run();
    }
  };

  return (
    <div
      className={`flex items-center space-x-[4px] justify-between w-full ${toolbarExtraClass}`}
    >
      {/* History Controls - Undo and Redo Buttons */}
      {mergedSchema.history && (
        <>
          <button
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            className="rounded-[4px] p-1 cursor-pointer hover:bg-hover-effect dark:hover:bg-hover-effect focus:bg-hover-effect dark:focus:bg-hover-effect focus:outline-none"
          >
            <UndoIcon className="w-5 h-5 text-dark-gray-fixed dark:text-dark-gray-fixed-dk" />
          </button>
          <button
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            className="rounded-[4px] p-1 cursor-pointer hover:bg-hover-effect dark:hover:bg-hover-effect focus:bg-hover-effect dark:focus:bg-hover-effect focus:outline-none"
          >
            <RedoIcon className="w-5 h-5 text-dark-gray-fixed dark:text-dark-gray-fixed-dk" />
          </button>
        </>
      )}

      {mergedSchema.history && (
        <div className="shrink-0 w-px h-[18px] bg-border-gray-fixed dark:bg-border-gray-fixed-dk" />
      )}

      {/* Typography Controls */}
      {typographySchema.fontFamily && (
        <div className="relative" ref={fontDropdownRef}>
          <button
            onClick={() => setIsFontOpen(!isFontOpen)}
            className="rounded-[4px] p-1 cursor-pointer hover:bg-hover-effect dark:hover:bg-hover-effect focus:bg-hover-effect dark:focus:bg-hover-effect focus:outline-none text-md text-verba-indigo-fixed dark:text-verba-indigo-fixed-dk font-semibold flex items-center justify-between space-x-[8px]"
          >
            <span className="truncate">
              {
                fontOptions.find((option) => option.value === selectedFont)
                  ?.label
              }
            </span>

            <ChevronDownIcon
              className={`w-[10px] h-[10px] text-dark-gray-fixed dark:text-dark-gray-fixed-dk transition-transform duration-200 ${
                isFontOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {fontPortal.renderPortal(
            <div className="min-w-[150px] bg-fixed-white dark:bg-fixed-white-dk border border-border-gray-fixed dark:border-border-gray-fixed-dk rounded-[4px] shadow-popper-blur-effect max-h-44 overflow-y-auto no-scrollbar">
              <ul>
                {fontOptions.map((option) => (
                  <li key={option.value}>
                    <button
                      onClick={() => handleFontChange(option.value)}
                      className={`w-full px-2 py-2 text-left text-md hover:bg-hover-effect dark:hover:bg-hover-effect focus:bg-hover-effect dark:focus:bg-hover-effect focus:outline-none flex items-center ${
                        selectedFont === option.value
                          ? "bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg text-verba-blue dark:text-verba-blue font-medium"
                          : "text-verba-indigo dark:text-verba-indigo"
                      }`}
                    >
                      <span className="w-5 mr-1">
                        {selectedFont === option.value && <CheckIcon />}
                      </span>
                      <span
                        style={{ fontFamily: option.value }}
                        className="text-verba-indigo dark:text-verba-indigo"
                      >
                        {option.label}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {typographySchema.fontFamily && (
        <div className="shrink-0 w-px h-[18px] bg-border-gray-fixed dark:bg-border-gray-fixed-dk" />
      )}

      {typographySchema.fontSize && (
        <div className="relative" ref={sizeDropdownRef}>
          <button
            onClick={() => setIsSizeOpen(!isSizeOpen)}
            className="rounded-[4px] p-1 cursor-pointer hover:bg-hover-effect dark:hover:bg-hover-effect focus:bg-hover-effect dark:focus:bg-hover-effect focus:outline-none flex items-center justify-between space-x-1"
          >
            <FontSizeIcon className="w-5 h-5 text-dark-gray-fixed dark:text-dark-gray-fixed-dk" />
            <ChevronDownIcon
              className={`w-[10px] h-[10px] text-dark-gray-fixed dark:text-dark-gray-fixed-dk transition-transform duration-200 ${
                isSizeOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {sizePortal.renderPortal(
            <div className="min-w-[120px] bg-fixed-white dark:bg-fixed-white-dk border border-border-gray-fixed dark:border-border-gray-fixed-dk rounded-[4px] shadow-popper-blur-effect">
              <ul>
                {sizeOptions.map((option) => (
                  <li key={option.value}>
                    <button
                      onClick={() => handleSizeChange(option.value)}
                      className={`w-full px-2 py-2 text-left text-md hover:bg-hover-effect dark:hover:bg-hover-effect focus:bg-hover-effect dark:focus:bg-hover-effect focus:outline-none flex items-center ${
                        selectedSize === option.value
                          ? "bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg text-verba-blue dark:text-verba-blue font-medium"
                          : "text-verba-indigo dark:text-verba-indigo"
                      }`}
                    >
                      <span className="w-5 mr-1">
                        {selectedSize === option.value && <CheckIcon />}
                      </span>
                      <span
                        style={{ fontSize: option.fontSize }}
                        className="text-verba-indigo dark:text-verba-indigo"
                      >
                        {option.label}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {typographySchema.fontSize && (
        <div className="shrink-0 w-px h-[18px] bg-border-gray-fixed dark:bg-border-gray-fixed-dk" />
      )}

      {/* Text Formatting Controls */}
      {(textFormattingSchema.bold ||
        textFormattingSchema.italic ||
        textFormattingSchema.underline ||
        textFormattingSchema.strikethrough) && (
        <div className="flex items-center space-x-[2px]">
          {textFormattingSchema.bold && (
            <Tooltip content="Bold" side="top">
              <div>
                <button
                  onClick={handleBoldToggle}
                  className={`${
                    activeFormatting.bold
                      ? "rounded-[4px] p-1 cursor-pointer bg-hover-effect dark:bg-hover-effect"
                      : "rounded-[4px] p-1 cursor-pointer hover:bg-hover-effect dark:hover:bg-hover-effect"
                  }`}
                >
                  <BoldIcon className="w-5 h-5 text-dark-gray-fixed dark:text-dark-gray-fixed-dk" />
                </button>
              </div>
            </Tooltip>
          )}
          {textFormattingSchema.italic && (
            <Tooltip content="Italic" side="top">
              <div>
                <button
                  onClick={handleItalicToggle}
                  className={`${
                    activeFormatting.italic
                      ? "rounded-[4px] p-1 cursor-pointer bg-hover-effect dark:bg-hover-effect"
                      : "rounded-[4px] p-1 cursor-pointer hover:bg-hover-effect dark:hover:bg-hover-effect"
                  }`}
                >
                  <ItalicIcon className="w-5 h-5 text-dark-gray-fixed dark:text-dark-gray-fixed-dk" />
                </button>
              </div>
            </Tooltip>
          )}
          {textFormattingSchema.underline && (
            <Tooltip content="Underline" side="top">
              <div>
                <button
                  onClick={handleUnderlineToggle}
                  className={`${
                    activeFormatting.underline
                      ? "rounded-[4px] p-1 cursor-pointer bg-hover-effect dark:bg-hover-effect"
                      : "rounded-[4px] p-1 cursor-pointer hover:bg-hover-effect dark:hover:bg-hover-effect"
                  }`}
                >
                  <UnderlineIcon className="w-5 h-5 text-dark-gray-fixed dark:text-dark-gray-fixed-dk" />
                </button>
              </div>
            </Tooltip>
          )}
          {textFormattingSchema.strikethrough && (
            <Tooltip content="Strikethrough" side="top">
              <div>
                <button
                  onClick={handleStrikeToggle}
                  className={`${
                    activeFormatting.strike
                      ? "rounded-[4px] p-1 cursor-pointer bg-hover-effect dark:bg-hover-effect"
                      : "rounded-[4px] p-1 cursor-pointer hover:bg-hover-effect dark:hover:bg-hover-effect"
                  }`}
                >
                  <StrikeThroughIcon className="w-5 h-5 text-dark-gray-fixed dark:text-dark-gray-fixed-dk" />
                </button>
              </div>
            </Tooltip>
          )}
        </div>
      )}

      {/* Text Styling Controls */}
      {textStylingSchema.textColor && (
        <div className="relative" ref={colorDropdownRef}>
          <button
            onClick={handleColorButtonClick}
            className="rounded-[4px] p-1 cursor-pointer hover:bg-hover-effect dark:hover:bg-hover-effect focus:bg-hover-effect dark:focus:bg-hover-effect focus:outline-none flex items-center justify-between space-x-1"
          >
            <TextColorPickerIcon className="w-5 h-5 text-dark-gray-fixed dark:text-dark-gray-fixed-dk" />
            <ChevronDownIcon
              className={`w-[10px] h-[10px] text-dark-gray-fixed dark:text-dark-gray-fixed-dk transition-transform duration-200 ${
                isColorOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {colorPortal.renderPortal(
            <div className="bg-fixed-white dark:bg-fixed-white-dk border border-border-gray-fixed dark:border-border-gray-fixed-dk rounded-[4px] shadow-popper-blur-effect">
              <ColorPicker
                editor={editor}
                onSetColor={handleSetColor}
                onUnsetColor={handleUnsetColor}
                onSetHighlight={handleSetHighlight}
                onUnsetHighlight={handleUnsetHighlight}
              />
            </div>
          )}
        </div>
      )}

      {(textFormattingSchema.bold ||
        textFormattingSchema.italic ||
        textFormattingSchema.underline ||
        textFormattingSchema.strikethrough ||
        textStylingSchema.textColor) && (
        <div className="shrink-0 w-px h-[18px] bg-border-gray-fixed dark:bg-border-gray-fixed-dk" />
      )}

      {contentStructureSchema.alignment && (
        <div className="relative" ref={alignDropdownRef}>
          <button
            onClick={() => setIsAlignOpen(!isAlignOpen)}
            className="flex items-center justify-between space-x-1 text-dark-gray-fixed dark:text-dark-gray-fixed-dk rounded-[4px] p-1 cursor-pointer hover:bg-hover-effect dark:hover:bg-hover-effect focus:bg-hover-effect dark:focus:bg-hover-effect focus:outline-none"
          >
            {selectedAlign === "left" ? (
              <AlignLeftIcon className="w-5 h-5" />
            ) : selectedAlign === "center" ? (
              <AlignCenterIcon className="w-5 h-5" />
            ) : (
              <AlignRightIcon className="w-5 h-5" />
            )}
            <ChevronDownIcon
              className={`w-[10px] h-[10px] text-dark-gray-fixed dark:text-dark-gray-fixed-dk transition-transform duration-200 ${
                isAlignOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {alignPortal.renderPortal(
            <div className="min-w-[45px] bg-fixed-white dark:bg-fixed-white-dk border border-border-gray-fixed dark:border-border-gray-fixed-dk rounded-[4px] shadow-popper-blur-effect">
              <ul>
                {alignOptions.map((option) => {
                  const IconComponent = option.icon;
                  return (
                    <li key={option.value}>
                      <button
                        onClick={() => handleAlignChange(option.value)}
                        className="w-full py-1 flex items-center justify-center "
                      >
                        <IconComponent className="w-5 h-5 text-dark-gray-fixed dark:text-dark-gray-fixed-dk" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Content Structure Controls */}
      {contentStructureSchema.lists && (
        <div className="relative" ref={listDropdownRef}>
          <Tooltip content="List" side="top">
            <div>
              <button
                onClick={() => setIsListOpen(!isListOpen)}
                className={`rounded-[4px] p-1 cursor-pointer hover:bg-hover-effect dark:hover:bg-hover-effect focus:bg-hover-effect dark:focus:bg-hover-effect focus:outline-none flex items-center justify-between space-x-1 ${
                  editor.isActive("bulletList") ||
                  editor.isActive("orderedList")
                    ? "rounded-[4px] p-1 cursor-pointer bg-hover-effect dark:bg-hover-effect"
                    : "rounded-[4px] p-1 cursor-pointer hover:bg-hover-effect dark:hover:bg-hover-effect text-dark-gray-fixed dark:text-dark-gray-fixed-dk"
                }`}
              >
                {selectedList === "bullet" ? (
                  <BulletedListIcon className="w-5 h-5" />
                ) : (
                  <NumberListIcon className="w-5 h-5" />
                )}
                <ChevronDownIcon
                  className={`w-[10px] h-[10px] text-dark-gray-fixed dark:text-dark-gray-fixed-dk transition-transform duration-200 ${
                    isListOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
            </div>
          </Tooltip>

          {listPortal.renderPortal(
            <div className="min-w-[45px] bg-fixed-white dark:bg-fixed-white-dk border border-border-gray-fixed dark:border-border-gray-fixed-dk rounded-[4px] shadow-popper-blur-effect">
              <ul>
                {listOptions.map((option) => {
                  const IconComponent = option.icon;
                  const isActive =
                    (option.value === "bullet" &&
                      editor.isActive("bulletList")) ||
                    (option.value === "number" &&
                      editor.isActive("orderedList"));

                  return (
                    <li key={option.value}>
                      <button
                        onClick={() => handleListChange(option.value)}
                        className={`w-full py-1 flex items-center justify-center ${
                          isActive
                            ? ""
                            : "text-dark-gray-fixed dark:text-dark-gray-fixed-dk"
                        }`}
                      >
                        <IconComponent className="w-5 h-5 text-dark-gray-fixed dark:text-dark-gray-fixed-dk" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}

      {(contentStructureSchema.alignment || contentStructureSchema.lists) && (
        <div className="shrink-0 w-px h-[18px] bg-border-gray-fixed dark:bg-border-gray-fixed-dk" />
      )}

      {contentStructureSchema.indentation && (
        <div className="flex items-center space-x-[2px]">
          <button
            onClick={handleOutdent}
            className="rounded-[4px] p-1 cursor-pointer hover:bg-hover-effect dark:hover:bg-hover-effect focus:bg-hover-effect dark:focus:bg-hover-effect focus:outline-none"
          >
            <IndentLeftIcon className="w-5 h-5 text-dark-gray-fixed dark:text-dark-gray-fixed-dk" />
          </button>
          <button
            onClick={handleIndent}
            className="rounded-[4px] p-1 cursor-pointer hover:bg-hover-effect dark:hover:bg-hover-effect focus:bg-hover-effect dark:focus:bg-hover-effect focus:outline-none"
          >
            <IndentRightIcon className="w-5 h-5 text-dark-gray-fixed dark:text-dark-gray-fixed-dk" />
          </button>
        </div>
      )}

      {contentStructureSchema.indentation &&
        (mediaSchema.links ||
          mediaSchema.images ||
          mediaSchema.attachments ||
          interactiveSchema.emojis ||
          utilitiesSchema.clearFormatting ||
          contentStructureSchema.blockquotes) && (
          <div className="shrink-0 w-px h-[18px] bg-border-gray-fixed dark:bg-border-gray-fixed-dk" />
        )}

      {/* Media Controls */}

      {mediaSchema.attachments && (
        <div className="flex items-center">
          <button
            onClick={handleAttachmentClick}
            disabled={isProcessingAttachments}
            className={`transition-colors ${
              isProcessingAttachments
                ? "rounded-[4px] p-1 cursor-pointer bg-hover-effect dark:bg-hover-effect"
                : "rounded-[4px] p-1 cursor-pointer text-dark-gray-fixed dark:text-dark-gray-fixed-dk hover:bg-hover-effect dark:hover:bg-hover-effect"
            }`}
            title={
              isProcessingAttachments
                ? "Processing attachments..."
                : "Attach files"
            }
          >
            <AttachmentIcon
              className={`w-5 h-5 text-dark-gray-fixed dark:text-dark-gray-fixed-dk ${
                isProcessingAttachments ? "animate-pulse" : ""
              }`}
            />
          </button>
          <input
            ref={attachmentInputRef}
            type="file"
            multiple
            onChange={handleAttachmentFileSelect}
            className="hidden"
          />
        </div>
      )}

      {mediaSchema.links && (
        <div className="flex items-center">
          <div className="relative" ref={linkPopoverRef}>
            <button
              onClick={handleLinkButtonClick}
              className={`flex items-center justify-center ${
                isLinkPopoverOpen
                  ? "w-7 h-6 rounded-[4px] p-1 cursor-pointer bg-hover-effect dark:bg-hover-effect"
                  : "w-7 h-6 rounded-[4px] p-1 cursor-pointer hover:bg-hover-effect dark:hover:bg-hover-effect"
              }`}
            >
              <AttachFilesIcon className="w-5 h-5 text-dark-gray-fixed dark:text-dark-gray-fixed-dk" />
            </button>
            {linkPortal.renderPortal(
              <div className="w-80 bg-fixed-white dark:bg-fixed-white-dk rounded-[4px] border border-border-gray-fixed dark:border-border-gray-fixed-dk shadow-popper-blur-effect p-3 space-y-3">
                <div className="space-y-1">
                  <input
                    id="link-text"
                    type="text"
                    value={linkText}
                    onChange={(e) => setLinkText(e.target.value)}
                    className="w-full px-2 py-1 border border-border-gray-fixed dark:border-border-gray-fixed-dk rounded focus:outline-none text-sm font-normal text-verba-indigo-fixed dark:text-verba-indigo-fixed-dk bg-fixed-white dark:bg-fixed-white-dk"
                    placeholder="Text"
                  />
                </div>
                <div className="space-y-1">
                  <input
                    id="link-url"
                    type="text"
                    value={linkUrl}
                    onChange={handleLinkUrlChange}
                    className={`w-full px-2 py-1 border border-border-gray-fixed dark:border-border-gray-fixed-dk rounded focus:outline-none text-sm font-normal text-verba-indigo-fixed dark:text-verba-indigo-fixed-dk bg-fixed-white dark:bg-fixed-white-dk ${
                      linkUrlError
                        ? "border-verba-red-alert dark:border-verba-red-alert-dk"
                        : "border-border-gray-fixed dark:border-border-gray-fixed-dk"
                    }`}
                    placeholder="Type or paste a link"
                    autoFocus
                  />
                  {linkUrlError && (
                    <div className="text-xs text-verba-red-alert dark:text-verba-red-alert-dk font-medium">
                      {linkUrlError}
                    </div>
                  )}
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={handleApplyLink}
                    disabled={!linkUrl || !linkText || !!linkUrlError}
                    className="disabled:opacity-50 px-4 py-1 text-sm font-medium text-fixed-white dark:text-fixed-white-dk bg-verba-blue dark:bg-verba-blue-dk rounded"
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {mediaSchema.images && (
        <div className="flex items-center">
          <button
            onClick={handleImageUpload}
            className="rounded-[4px] p-1 cursor-pointer hover:bg-hover-effect dark:hover:bg-hover-effect focus:bg-hover-effect dark:focus:bg-hover-effect focus:outline-none"
          >
            <ImageIcon className="w-5 h-5 text-dark-gray-fixed dark:text-dark-gray-fixed-dk" />
          </button>
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageFileSelect}
            className="hidden"
          />
        </div>
      )}

      {/* Interactive Controls */}
      {interactiveSchema.emojis && (
        <div className="relative" ref={emojiDropdownRef}>
          <button
            onClick={handleEmojiToggle}
            className={`flex items-center justify-center ${
              isEmojiOpen
                ? "w-7 h-7 rounded-[4px] p-1 cursor-pointer bg-hover-effect dark:bg-hover-effect"
                : "w-7 h-7 rounded-[4px] p-1 cursor-pointer hover:bg-hover-effect dark:hover:bg-hover-effect text-dark-gray-fixed dark:text-dark-gray-fixed-dk"
            }`}
          >
            <EmojiIcon className="w-5 h-5 text-dark-gray-fixed dark:text-dark-gray-fixed-dk" />
          </button>

          {emojiPortal.renderPortal(
            <EmojiPicker editor={editor} onClose={() => setIsEmojiOpen(false)} />
          )}
        </div>
      )}

      {/* Utility Controls */}
      {(utilitiesSchema.clearFormatting ||
        contentStructureSchema.blockquotes) && (
        <div className="flex items-center space-x-[2px]">
          {formattingOptions.map((option) => {
            // Check if this formatting option should be shown
            const shouldShow =
              (option.value === "remove" && utilitiesSchema.clearFormatting) ||
              (option.value === "quote" && contentStructureSchema.blockquotes);

            if (!shouldShow) return null;

            const IconComponent = option.icon;
            const isActive =
              option.value === "quote" ? editor.isActive("blockquote") : false;

            return (
              <button
                key={option.value}
                onClick={option.action}
                className={`${
                  isActive
                    ? "rounded-[4px] p-1 cursor-pointer bg-hover-effect dark:bg-hover-effect"
                    : "rounded-[4px] p-1 cursor-pointer hover:bg-hover-effect dark:hover:bg-hover-effect text-dark-gray-fixed dark:text-dark-gray-fixed-dk"
                }`}
              >
                <IconComponent className={option.iconClassName || "w-5 h-5"} />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Toolbar;
